import _slicedToArray from "/Users/vu/Documents/Repos.nosync/unfair-machine/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
/**
 * @license
 * Copyright 2020 Google LLC. All Rights Reserved.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * =============================================================================
 */
import { ResizeNearestNeighbor, util } from '@tensorflow/tfjs-core';
import { assertNotComplex } from '../cpu_util';
export function resizeNearestNeighbor(args) {
  var inputs = args.inputs,
    backend = args.backend,
    attrs = args.attrs;
  var images = inputs.images;
  var alignCorners = attrs.alignCorners,
    halfPixelCenters = attrs.halfPixelCenters,
    size = attrs.size;
  assertNotComplex(images, 'resizeNearestNeighbor');
  var imagesStrides = util.computeStrides(images.shape);
  var _size = _slicedToArray(size, 2),
    newHeight = _size[0],
    newWidth = _size[1];
  var _images$shape = _slicedToArray(images.shape, 4),
    batch = _images$shape[0],
    oldHeight = _images$shape[1],
    oldWidth = _images$shape[2],
    numChannels = _images$shape[3];
  var xValues = backend.data.get(images.dataId).values;
  var output = new Float32Array(batch * newHeight * newWidth * numChannels);
  var effectiveInputSize = [alignCorners && newHeight > 1 ? oldHeight - 1 : oldHeight, alignCorners && newWidth > 1 ? oldWidth - 1 : oldWidth];
  var effectiveOutputSize = [alignCorners && newHeight > 1 ? newHeight - 1 : newHeight, alignCorners && newWidth > 1 ? newWidth - 1 : newWidth];
  var effectiveRowSizeRatio = effectiveInputSize[0] / effectiveOutputSize[0];
  var effectiveColSizeRatio = effectiveInputSize[1] / effectiveOutputSize[1];
  var outputOffset = 0;
  for (var b = 0; b < batch; b++) {
    var batchOffset = b * imagesStrides[0];
    for (var r = 0; r < newHeight; r++) {
      var sourceFracRow = halfPixelCenters ? effectiveRowSizeRatio * (r + 0.5) : effectiveRowSizeRatio * r;
      var sourceNearestRow = Math.min(oldHeight - 1, alignCorners ? Math.round(sourceFracRow) : Math.floor(sourceFracRow));
      if (halfPixelCenters) {
        sourceNearestRow = Math.max(0, sourceNearestRow);
      }
      var rowOffset = batchOffset + sourceNearestRow * imagesStrides[1];
      for (var c = 0; c < newWidth; c++) {
        var sourceFracCol = halfPixelCenters ? effectiveColSizeRatio * (c + 0.5) : effectiveColSizeRatio * c;
        var sourceNearestCol = Math.min(oldWidth - 1, alignCorners ? Math.round(sourceFracCol) : Math.floor(sourceFracCol));
        if (halfPixelCenters) {
          sourceNearestCol = Math.max(0, sourceNearestCol);
        }
        var colOffset = rowOffset + sourceNearestCol * imagesStrides[2];
        for (var d = 0; d < numChannels; d++) {
          // Begin shader.
          // Compute the fractional index of the source.
          var newVal = xValues[colOffset + d];
          output[outputOffset++] = newVal;
        }
      }
    }
  }
  return backend.makeTensorInfo([batch, newHeight, newWidth, numChannels], images.dtype, output);
}
export var resizeNearestNeighborConfig = {
  kernelName: ResizeNearestNeighbor,
  backendName: 'cpu',
  kernelFunc: resizeNearestNeighbor
};